import React, { useState, useEffect, useCallback, useRef } from 'react';
import { websocketVirtualAgent } from 'components/virtualagentws/websocketVirtualAgent';
import { useHVAConversation } from './HVADebtCollectionContext';
import { FeedbackMessage } from './HVADebtCollectionData';
import { Col, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getVirtualAgentWsUrl,
  getAuthenticatedWsUrl
} from 'services/virtualagent.service';
import {
  faMicrophone,
  faMicrophoneSlash,
  faPhoneSlash,
  faPhoneVolume
} from '@fortawesome/free-solid-svg-icons';

interface ConversationMessage {
  source: 'user' | 'ai';
  message: string;
}

type HVAConversationWidgetProps = {
  agentId: string;
  agentName: string;
  onError?: (error: Error) => void;
  onFeedback: (message: Omit<FeedbackMessage, 'id' | 'timestamp'>) => void;
  firstMessage: string;
  prompt: string;
  voice: string;
  language: string;
};

const HVADynamicConversationWidget = ({
  agentId,
  agentName,
  onError,
  onFeedback,
  firstMessage,
  prompt,
  voice,
  language
}: HVAConversationWidgetProps): JSX.Element => {
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [clientToolsConfig, setClientToolsConfig] = useState({});

  // Obtenemos el contexto de la conversación
  // eslint-disable-next-line
  const { clientInfo, setIsNifValidated, setIsDobValidated, setRequestTransfer } = useHVAConversation();

  // Referencia para el contenedor de mensajes
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Función para hacer scroll hasta el último mensaje
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const scrollContainer = messagesContainerRef.current;
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  };

  // Actualizamos clientTools cuando cambian las validaciones locales
  useEffect(() => {
    let localNifValidated = false;
    let localDobValidated = false;

    // Primero añadimos la función de limpieza de TRN
    const cleanNIF = (nif: string): string => {
      return nif.replace(/[-.\s]/g, '').toUpperCase();
    };

    setClientToolsConfig({
      requestAction: async (parameters: { requestedAction: string }) => {
        const { requestedAction } = parameters;
        switch (requestedAction) {
          case 'TRANSFERTOSUPERVISOR': {
            setRequestTransfer(true);
            onFeedback({
              type: 'transfer',
              message: 'Transfer to human supervisor requested',
              success: true
            });
            return JSON.stringify({
              success: true,
              message: 'Transfer requested'
            });
          }
          case 'TRANSFERTOPOS': {
            setRequestTransfer(true);
            onFeedback({
              type: 'transfer',
              message: 'Transfer to POS requested',
              success: true
            });
            return JSON.stringify({
              success: true,
              message: 'Transfer requested'
            });
          }
        }
      },

      requestInfo: async (parameters: { requestedInfo: string }) => {
        const { requestedInfo } = parameters;
        switch (requestedInfo) {
          case 'NAME': {
            onFeedback({
              type: 'info',
              message: 'Client name requested',
              details: `Name: ${clientInfo?.name || 'Not found'}`,
              success: !!clientInfo?.name
            });
            return JSON.stringify({
              success: true,
              data: `CustomerName: ${clientInfo?.name}`,
              message: ''
            });
          }

          case 'DATETIME': {
            // Retorna fecha y hora actuales
            const now = new Date();
            const options: Intl.DateTimeFormatOptions = {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            };
            const dateTimeString = now.toLocaleString('en-GB', options);

            onFeedback({
              type: 'info',
              message: 'Solicitud de fecha y hora',
              details: `Fecha y hora actual: ${dateTimeString}`,
              success: true
            });

            return JSON.stringify({
              success: true,
              data: dateTimeString,
              message: ''
            });
          }

          case 'DEBTDETAILS': {
            try {
              if (!localNifValidated) {
                onFeedback({
                  type: 'info',
                  message: 'Debt information requested',
                  // eslint-disable-next-line
                  details: 'TRN validation required before accessing debt details',
                  success: false
                });
                return JSON.stringify({
                  success: false,
                  data: null,
                  message: 'Please validate customer TRN first'
                });
              }

              if (!localDobValidated) {
                onFeedback({
                  type: 'info',
                  message: 'Debt information requested',
                  // eslint-disable-next-line
                  details: 'Date of birth validation required before accessing debt details',
                  success: false
                });
                return JSON.stringify({
                  success: false,
                  data: null,
                  message: 'Please validate customer date of birth first'
                });
              }

              onFeedback({
                type: 'info',
                message: 'Debt information requested',
                success: true
              });

              return JSON.stringify({
                success: true,
                data: {
                  amount: `${clientInfo?.debtamount}`,
                  currency: `${clientInfo?.currency}`,
                  origin: `${clientInfo?.origin}`,
                  max_discount: `${clientInfo?.maxDiscount}`
                },
                message: ''
              });
            } catch (error) {
              // eslint-disable-next-line
              const errorMessage = error instanceof Error ? error.message : 'Unknown error';
              onFeedback({
                type: 'info',
                message: 'Debt information requested',
                details: errorMessage,
                success: false
              });
              return JSON.stringify({
                success: false,
                data: null,
                message: errorMessage
              });
            }
          }

          case 'IBAN': {
            if (!localNifValidated) {
              onFeedback({
                type: 'info',
                message: 'IBAN requested',
                details: 'TRN validation required before accessing IBAN',
                success: false
              });
              return JSON.stringify({
                success: false,
                data: null,
                message: 'Please validate customer TRN first'
              });
            }

            if (!localDobValidated) {
              onFeedback({
                type: 'info',
                message: 'IBAN requested',
                // eslint-disable-next-line
                details: 'Date of birth validation required before accessing IBAN',
                success: false
              });
              return JSON.stringify({
                success: false,
                data: null,
                message: 'Please validate customer date of birth first'
              });
            }

            onFeedback({
              type: 'info',
              message: 'IBAN requested',
              details: `IBAN: ${clientInfo?.IBAN || 'Not found'}`,
              success: !!clientInfo?.IBAN
            });
            return JSON.stringify({
              success: true,
              data: `IBAN: ${clientInfo?.IBAN}`,
              message: ''
            });
          }

          default: {
            onFeedback({
              type: 'info',
              message: `Requested information not available: ${requestedInfo}`,
              details: 'Requested information not available',
              success: false
            });
            return JSON.stringify({
              success: false,
              data: null,
              message: `Requested information not available: '${requestedInfo}'`
            });
          }
        }
      },

      requestValidation: async (parameters: {
        requestedValidation: string;
        valueProvided: string;
      }) => {
        const { requestedValidation, valueProvided } = parameters;

        switch (requestedValidation) {
          case 'NIF': {
            const cleanedNIF = cleanNIF(valueProvided);
            const formNIF = clientInfo?.trn ? cleanNIF(clientInfo.trn) : '';
            const isValid = formNIF === cleanedNIF;

            setIsNifValidated(isValid);
            localNifValidated = isValid;

            onFeedback({
              type: 'validation',
              message: 'NIF validation requested',
              // eslint-disable-next-line
              details: `NIF provided: ${valueProvided} Result: ${isValid ? '✓ NIF matches' : '✗ NIF does not match'}`,
              success: isValid
            });

            return JSON.stringify({
              success: isValid,
              message: isValid ? 'NIF matches' : 'NIF does not match',
              validatedNif: isValid ? cleanedNIF : null
            });
          }

          case 'DATEOFBIRTH': {
            if (!valueProvided || !clientInfo?.dateofbirth) {
              onFeedback({
                type: 'validation',
                message: 'Date of birth validation requested',
                details: 'Incomplete data for validation',
                success: false
              });
              return JSON.stringify({
                success: false,
                message: 'Incomplete data for validation',
                validatedDate: null
              });
            }

            try {
              const formDateStr = clientInfo.dateofbirth.replace(/-/g, '');

              if (!/^\d{8}$/.test(valueProvided)) {
                throw new Error('Invalid date provided');
              }

              const isValid = formDateStr === valueProvided;
              setIsDobValidated(isValid);
              localDobValidated = isValid;

              onFeedback({
                type: 'validation',
                message: 'Date of birth validation requested',
                // eslint-disable-next-line
                details: `DoB: ${formDateStr} = Date provided: ${valueProvided} Result: ${isValid ? '✓ Matches' : '✗ Does not match'}`,
                success: isValid
              });

              return JSON.stringify({
                success: isValid,
                // eslint-disable-next-line
                message: isValid ? 'Date of birth matches' : 'Date of birth does not match',
                validatedDate: isValid ? valueProvided : null
              });
            } catch (error) {
              onFeedback({
                type: 'validation',
                message: 'Date of birth validation error',
                details: 'Invalid date format',
                success: false
              });
              return JSON.stringify({
                success: false,
                message: 'Invalid date format',
                validatedDate: null
              });
            }
          }

          default: {
            onFeedback({
              type: 'validation',
              message: `Validation requested not available: ${requestedValidation}`,
              details: 'Validation requested not available',
              success: false
            });
            return JSON.stringify({
              success: false,
              data: null,
              message: `Validation requested not available: '${requestedValidation}'`
            });
          }
        }
      },

      END_CALL: async (parameters: { reason: string }) => {
        // Añadimos mensaje de feedback
        onFeedback({
          type: 'close',
          message: 'Finalización de llamada por el agente',
          details: `Motivo: ${parameters.reason}`,
          success: true
        });

        return JSON.stringify({
          success: true,
          message: `Finalización de llamada`
        });
      }
    });
    // eslint-disable-next-line
  }, [clientInfo, onFeedback, setIsNifValidated, setIsDobValidated, setRequestTransfer]);

  // Para adaptarnos a las instrucciones del agente
  const processFirstMessage = (
    message: string,
    agentName: string,
    customerName: string
  ): string => {
    const getSalutation = () => {
      const hour = new Date().getHours();
      if (hour < 13) return 'Buenos días';
      if (hour < 20) return 'Buenas tardes';
      return 'Buenas noches';
    };

    return message
      .replace('{{salutationDayTime}}', getSalutation())
      .replace('{{agentName}}', agentName)
      .replace('{{customerName}}', customerName);
  };

  const conversation = websocketVirtualAgent({
    onConnect: () => {
      console.log('Connected to agent');
      setIsInitialized(true);
      setIsLoading(false);
    },
    onDisconnect: () => {
      console.log('Disconnected from agent');
      onFeedback({
        type: 'close',
        message: 'Hangup executed',
        success: true
      });
      setIsInitialized(false);
      setIsLoading(false);
      setIsNifValidated(false);
      setIsDobValidated(false);
    },
    // eslint-disable-next-line
    onError: (message: string, context?: any) => {
      console.error('Conversation error:', message, context);
      setIsLoading(false);
      if (onError) {
        onError(new Error(message));
      }
    },
    onMessage: message => {
      setMessages(prev => [
        ...prev,
        {
          source: message.source,
          message: message.message
        }
      ]);
      setTimeout(scrollToBottom, 0);
    },
    // Añadimos la configuración de client tools
    clientTools: clientToolsConfig
  });

  const requestMicPermission = async (): Promise<boolean> => {
    try {
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      setHasPermission(true);
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      setHasPermission(false);
      if (onError) {
        onError(new Error('Microphone permission denied'));
      }
      return false;
    }
  };

  const startConversation = useCallback(async (): Promise<void> => {
    if (!hasPermission) {
      const permitted = await requestMicPermission();
      if (!permitted) return;
    }

    try {
      setIsLoading(true);
      setIsNifValidated(false);
      setIsDobValidated(false);
      await conversation.endSession();

      // Obtener URL backend
      let signedUrl;
      if (import.meta.env.VITE_NODE_ENV === 'production') {
        // En producción, usar la función asíncrona
        signedUrl = await getAuthenticatedWsUrl();
      } else {
        // En desarrollo, usar la URL directa
        signedUrl = getVirtualAgentWsUrl();
      }

      // Iniciar sesión con URL firmada
      setMessages([]);
      await conversation.startSession({
        signedUrl,
        overrides: {
          agent: {
            prompt: {
              prompt: prompt
            },
            firstMessage: processFirstMessage(
              firstMessage,
              agentName,
              clientInfo?.name || ''
            ),
            language: language,
            tools: [
              {
                id: 'requestAction',
                name: 'requestAction',
                description: 'Request a specific action to be performed',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedAction: {
                      type: 'string',
                      description:
                        'The action to request (TRANSFERTOSUPERVISOR, TRANSFERTOPOS, END_CALL)'
                    }
                  },
                  required: ['requestedAction']
                }
              },
              {
                id: 'requestInfo',
                name: 'requestInfo',
                description:
                  'Request specific information about the customer or situation',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedInfo: {
                      type: 'string',
                      description:
                        'The type of information to request (NAME, DATETIME, DEBTDETAILS, IBAN)'
                    }
                  },
                  required: ['requestedInfo']
                }
              },
              {
                id: 'requestValidation',
                name: 'requestValidation',
                description: 'Validate customer provided information',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedValidation: {
                      type: 'string',
                      description:
                        'The type of validation to perform (e.g., NIF, DATEOFBIRTH)'
                    },
                    valueProvided: {
                      type: 'string',
                      description:
                        'The value provided by the customer. With DATEOFBIRTH, inform valueProvided with format YYYYMMDD'
                    }
                  },
                  required: ['requestedValidation', 'valueProvided']
                }
              },
              {
                id: 'END_CALL',
                name: 'END_CALL',
                description: 'Ends current conversation',
                parameters: {
                  type: 'object',
                  properties: {
                    reason: {
                      type: 'string',
                      description: 'The reason for ending the call'
                    }
                  },
                  required: []
                }
              }
            ]
          },
          tts: {
            voiceId: clientInfo?.voice || voice
          }
        }
      });
    } catch (error) {
      console.error('Failed to start conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [agentId, conversation, hasPermission, onError]);

  const endConversation = useCallback(async (): Promise<void> => {
    try {
      await conversation.endSession();
    } catch (error) {
      console.error('Failed to end conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [conversation, onError]);

  // Efecto para inicializar el scroll cuando se montan los mensajes
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Efecto para acceder al micrófono
  useEffect(() => {
    void requestMicPermission();
    return () => {
      if (isInitialized) {
        void endConversation();
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center p-0">
      <Row className="mb-2 mt-2">
        <Col className="pe-0" style={{ maxWidth: '170px', width: '170px' }}>
          <div className="d-flex bg-primary rounded-5 w-100 justify-content-center">
            {!isInitialized ? (
              <Button
                onClick={() => void startConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
                disabled={!hasPermission || isLoading}
              >
                <FontAwesomeIcon icon={faPhoneVolume} className="me-2 fs-8" />
                {isLoading ? 'Connecting...' : 'Start call'}
              </Button>
            ) : (
              <Button
                onClick={() => void endConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
              >
                <FontAwesomeIcon icon={faPhoneSlash} className="me-2 fs-8" />
                End call
              </Button>
            )}
          </div>
        </Col>
        <Col className="d-flex justify-content-end">
          <div className="d-flex justify-content-end text-end fs-10 me-2">
            <Row className="text-body-secondary">
              <div className="pt-1">
                Connected:
                {conversation.status === 'connected' ? (
                  <FontAwesomeIcon
                    icon={faPhoneVolume}
                    className="mx-2 me-0 fs-10 text-success"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faPhoneSlash}
                    className="mx-2 me-0 fs-10 text-danger"
                  />
                )}
              </div>
              <div>
                {conversation.status === 'connected' ? (
                  <>
                    Status:
                    {conversation.isSpeaking ? (
                      <FontAwesomeIcon
                        icon={faMicrophone}
                        className="mx-2 me-1 fs-10 text-success"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faMicrophoneSlash}
                        className="mx-1 me-0 fs-10 text-danger"
                      />
                    )}
                  </>
                ) : null}
              </div>
            </Row>
          </div>
        </Col>
      </Row>

      <div
        ref={messagesContainerRef}
        className="p-3 pt-2 pb-2 border rounded scrollbar bg-body-emphasis fs-9 lh-sm shadow-sm"
        style={{
          height: '240px',
          overflowY: 'auto',
          scrollBehavior: 'smooth'
        }}
      >
        {messages.length > 0 && (
          <div className="w-full space-y-2">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`p-2 rounded mb-2 ${
                  msg.source === 'user' ? 'bg-body' : 'bg-body-emphasis'
                }`}
              >
                <strong>{msg.source === 'user' ? 'Customer' : 'Agent'}:</strong>
                &nbsp;
                {msg.message}
              </div>
            ))}
          </div>
        )}
      </div>
      {!hasPermission && (
        <div className="fs-9 p-2 bg-body rounded-lg mt-2">
          Microphone permission is required for conversation
        </div>
      )}
    </div>
  );
};

export default HVADynamicConversationWidget;
